import { getMapAssets } from '../../domain/map/mapAssets.js';
import {
  buildMinimapMarkers,
  MINIMAP_ISO_DEG
} from '../../domain/map/minimapProjection.js';
import { buildPingMinimapMarkers } from '../../domain/map/pingMinimapMarkers.js';
import { createMinimapController } from './minimapController.js';
import { wireMinimapPingClick } from './wireMinimapPingClick.js';
import {
  loadClientSettingsSnapshot,
  mergeClientSettingsSnapshot
} from '../settings/persistClientSettings.js';

/**
 * bootMinimap — Wire AR-06 minimap without bloating app.js.
 */
function listActivePings(getPings) {
  if (typeof getPings === 'function') return getPings() || [];
  const hook = globalThis.__debugPing;
  if (hook && typeof hook.list === 'function') return hook.list() || [];
  return [];
}

export function bootMinimap({
  rootEl,
  canvasEl,
  matchRuntime,
  mapObjectives,
  mapBounds,
  getPings,
  storage = typeof globalThis.localStorage !== 'undefined'
    ? globalThis.localStorage
    : null,
  globalObj = typeof window !== 'undefined' ? window : null
} = {}) {
  const minimap = createMinimapController({
    rootEl,
    canvasEl,
    getBounds: () => mapBounds,
    getMarkers: () => {
      const base = buildMinimapMarkers({
        entities: matchRuntime.entities.getAll(),
        relics: mapObjectives.getRelics()
      });
      return base.concat(buildPingMinimapMarkers(listActivePings(getPings)));
    }
  });

  const pingSnap = loadClientSettingsSnapshot(storage) || {};
  const pingClick = wireMinimapPingClick({
    canvasEl,
    getBounds: () => mapBounds,
    getAngleDeg: () => {
      const mapId = minimap.getMapId();
      if (!mapId) return 0;
      const assets = getMapAssets(mapId);
      return assets.silhouette === 'bridge' ? MINIMAP_ISO_DEG : 0;
    },
    initialCycleIndex: pingSnap.pingCycleIndex,
    defaultType: pingSnap.defaultPingType || 'danger',
    onCycleIndexChange: (index, type) => {
      mergeClientSettingsSnapshot(storage, {
        pingCycleIndex: index,
        defaultPingType: type
      });
    },
    globalObj
  });

  if (globalObj) {
    globalObj.__minimap = {
      ...minimap,
      clickEmit: pingClick.handleClick,
      getPingCycleIndex: pingClick.getPingCycleIndex,
      getPingType: pingClick.getPingType,
      cyclePingType: pingClick.cyclePingType,
      setPingCycleIndex: pingClick.setPingCycleIndex,
      getPingCycleSnapshot: pingClick.getPingCycleSnapshot
    };
    globalObj.__debugPingCycle = {
      get: () => pingClick.getPingCycleSnapshot(),
      set: (n) => pingClick.setPingCycleIndex(n),
      cycle: () => pingClick.cyclePingType(),
      load: () => loadClientSettingsSnapshot(storage)
    };
  }

  return {
    ...minimap,
    clickEmit: pingClick.handleClick,
    getPingCycleIndex: pingClick.getPingCycleIndex,
    getPingType: pingClick.getPingType,
    cyclePingType: pingClick.cyclePingType,
    setPingCycleIndex: pingClick.setPingCycleIndex,
    getPingCycleSnapshot: pingClick.getPingCycleSnapshot
  };
}
